//@flow
import React, { useEffect } from 'react';
import MediaQuery from 'react-responsive';
import SimpleBar from 'simplebar-react';
import MobileHeader from 'components/atoms/mobileHeader';
import NeighbourhoodOverlay from 'components/molecules/neighbourhoodOverlay';
import GridView from 'components/organisms/gridView';
import { ImageOverlay } from 'components/organisms/imageOverlay';
import MainMenuDesktop from 'components/organisms/mainMenuDesktop';
import { MainMenuMobile } from 'components/organisms/mainMenuMobile';
import { OurHomesMobileDescriptionOverlay } from 'components/organisms/detailPageMobileOverlay';
import OurHomesDescriptionOverlay from 'components/organisms/detailPageWebOverlay';
import { OverlayHeader } from 'components/molecules/overlayHeader';
import { VariationsOverlay } from 'components/molecules/variationsOverlay';
import { VideoOverlay } from 'components/organisms/videoOverlay';
import { useViewport } from 'components/templates/viewportProvider';
import closeIconBlack from 'assets/images/close.png';
import closeIconWhite from 'assets/images/closewhite.png';
import squirrelIcon from 'assets/images/squirrel.svg';
import squirrelWhiteIcon from 'assets/images/squirrelWhite.svg';
import type { GalleryImageType, SiteSettings, Vertical } from 'types/common';
import PastEventGrid from 'components/molecules/pastEventsGrid';
import type { Neighbourhood } from 'types/project';
import { HotspotOverlay } from 'components/molecules/hotspotOverlay';
import { HotspotOverlayMobile } from 'components/molecules/hotspotOverlayMobile';
import { NewsPublicationOverlay } from 'components/organisms/newsPublicationOverlay';
import UpcomingEventsOverlayMobile from 'components/organisms/upcomingEventsOverlayMobile';
import UpcomingEventsOverlay from 'components/organisms/upcomingEventsOverlay';
import { ArchitectProjectOverlay } from 'components/organisms/architectProjectOverlay';
import { ArchitectProjectOverlayMobile } from 'components/organisms/architectProjectOverlayMobile';
import { ReservationFormOverlay } from 'components/templates/reservationSection';
import { RestaurantMobileMenuOverlay } from 'components/molecules/restaurantMobileHeaderOverlay';
import EventTicketSelection from 'components/organisms/eventTicketSelection';
import 'animate.css/animate.css';
import 'simplebar/dist/simplebar.min.css';
import './styles.scss';
import RentalsOverlay from 'components/molecules/rentalsOverlay';

// TODO: Needs type & data validations
type Props = {
  theme: string,
  type: string,
  onClosePress: () => void,
  mediaPath?: string,
  breadcrumbList: Array,
  siteSettings?: SiteSettings,
  verticals?: Array<Vertical>,
  activeImage?: GalleryImageType,
  images?: Array<GalleryImageType>,
  hideGutter?: boolean,
  className?: string,
  activeFilters?: Array<string>,
  filterIcon?: string,
  showFilter?: boolean,
  filteredNeighbourHood?: Array<Neighbourhood>,
  hideTopGutter?: boolean,
};

export const OverlayModal = (props: Props) => {
  const {
    data,
    theme,
    type,
    onClosePress,
    mediaPath,
    breadcrumbList,
    openFor,
    siteSettings,
    verticals = [],
    activeImage = null,
    images = [],
    hideGutter = false,
    className,
    activeFilters = [],
    filterIcon,
    showFilter = true,
    filteredNeighbourHood,
    fullPageApi,
    hideTopGutter = false,
    headerClass,
    awardData,
    formData,
    location,
    onSelect,
    value,
    placeholder,
    extractDates,
    updateOverlay
  } = props;
  const { isMobile } = useViewport();
  const icon = theme === 'light-theme' ? squirrelIcon : squirrelWhiteIcon;
  const closeIcon = theme === 'light-theme' ? closeIconBlack : closeIconWhite;

  const renderContent = () => {
    return (() => {
      switch (type) {
        case 'menu':
          return (
            <MainMenuDesktop
              theme={theme}
              onClosePress={onClosePress}
              verticals={verticals}
              siteSettings={siteSettings}
              fullPageApi={fullPageApi}
            />
          );
        case 'projects':
        case 'products':
        case 'view-all-roles':
        case 'architecture-projects':
          return <GridView data={data} mediaPath={mediaPath} type={type} />;
        case 'leadership-overlay':
        case 'description-overlay':
          return (
            <OurHomesDescriptionOverlay
              data={data}
              type={type}
              openFor={openFor}
            />
          );
        case 'image-overlay':
          return <ImageOverlay activeImage={activeImage} images={images} />;
        case 'video-overlay':
          return (
            <VideoOverlay data={data} type={type} onClosePress={onClosePress} />
          );
        case 'hotspot-overlay':
          return <HotspotOverlay data={data} />;
        case 'past-events':
          return <PastEventGrid data={data} />;
        case 'news-publications-overlay':
          return <NewsPublicationOverlay data={data} />;
        case 'upcoming-events-desktop':
          return <UpcomingEventsOverlay data={data} />;
        case 'architect-project':
          return <ArchitectProjectOverlay data={data} awardData={awardData} />;
        case 'reservation-form':
          return (
            <ReservationFormOverlay
              data={data}
              formData={formData}
              location={location}
              type={'no-padding'}
              descriptionType={'reservation'}
            />
          );
        case 'ticket-selection':
          return (
            <EventTicketSelection
              event_details={data}
              onContactUsClick={onClosePress}
              extractDates={extractDates}
              onSelect={onSelect}
              placeholder={placeholder}
              value={value}
              updateOverlay={updateOverlay}
            />
          );
        case 'rentals':
          return <RentalsOverlay overlayData={data} />;
        default:
          return null;
      }
    })();
  };

  const renderMobileContent = () => {
    return (() => {
      switch (type) {
        case 'menu':
          return (
            <MainMenuMobile
              theme={theme}
              verticals={verticals}
              siteSettings={siteSettings}
              onClosePress={onClosePress}
              fullPageApi={fullPageApi}
            />
          );
        case 'image-overlay':
          return <ImageOverlay activeImage={activeImage} images={images} />;
        case 'versions-overlay':
          return <VariationsOverlay data={data} />;
        case 'mobile-hotspot-overlay':
          return <HotspotOverlayMobile data={data} />;
        case 'leadership-mobile-overlay':
        case 'description-overlay':
          return (
            <OurHomesMobileDescriptionOverlay
              data={data}
              type={type}
              openFor={openFor}
              className={className}
            />
          );
        case 'upcoming-events-mobile':
          return <UpcomingEventsOverlayMobile data={data} />;
        case 'video-overlay':
          return <VideoOverlay data={data} onClosePress={onClosePress} />;
        case 'architect-project':
          return (
            <ArchitectProjectOverlayMobile data={data} awardData={awardData} />
          );
        case 'reservation-form':
          return (
            <ReservationFormOverlay
              data={data}
              formData={formData}
              location={location}
              descriptionType={'reservation'}
            />
          );
        case 'restaurant-header-buttons':
          return (
            <RestaurantMobileMenuOverlay
              data={data}
              descriptionType={'reservation'}
            />
          );
        case 'ticket-selection':
          return (
            <EventTicketSelection
              event_details={data}
              onContactUsClick={onClosePress}
              extractDates={extractDates}
              onSelect={onSelect}
              placeholder={placeholder}
              value={value}
              updateOverlay={updateOverlay}
            />
          );
        default:
          return null;
      }
    })();
  };

  return (
    <>
      <MediaQuery minDeviceWidth={768}>
        <div className="main-menu normal-scroll">
          <div className={`overlay-container ${theme}`}>
            <div className={'squirrel-icon-container'}>
              <img className={'squirrel-icon-img'} src={icon} alt={'icon'} />
            </div>
            <OverlayHeader
              theme={theme}
              breadcrumbList={breadcrumbList}
              onMenuPress={onClosePress}
              isMobile={isMobile}
              type={type}
              activeFilters={activeFilters}
              showFilter={showFilter}
              headerClass={headerClass}
            />
            <div
              className={`overlay-content ${hideGutter ? 'no-gutter' : ''} ${
                hideTopGutter ? 'no-top-gutter' : ''
              } ${className ? className : ''} ${
                type === 'menu' ? 'hide-left-padding' : ''
              }`}
              // style={{ height }}
            >
              {type && type === 'neighbourhood' ? (
                <NeighbourhoodOverlay
                  data={data}
                  mediaPath={mediaPath}
                  filterImage={filterIcon}
                  filteredNeighbourHood={filteredNeighbourHood}
                />
              ) : (
                <SimpleBar autoHide={false} style={{ height: '100%' }}>
                  {renderContent()}
                </SimpleBar>
              )}
            </div>
          </div>
        </div>
      </MediaQuery>
      <MediaQuery maxDeviceWidth={767}>
        <div
          className={`normal-scroll main-menu animated fadeInLeftSmall ${theme}`}
        >
          <div className={'full'}>
            <MobileHeader
              onClick={props.onClosePress}
              breadcrumbList={breadcrumbList}
              icon={
                <img
                  className={'close-icon-img'}
                  src={closeIcon}
                  alt={'icon'}
                />
              }
            />
            <div className={'squirrel-icon-container'}>
              <img className={'squirrel-icon-img'} src={icon} alt={'icon'} />
            </div>
            <SimpleBar autoHide={false} style={{ height: '100%' }}>
              <div
                className={`overlay-content ${hideGutter ? 'no-gutter' : ''} ${
                  className ? className : ''
                }`}
              >
                {renderMobileContent()}
              </div>
            </SimpleBar>
          </div>
        </div>
      </MediaQuery>
    </>
  );
};
